import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppLayoutModule } from './layout/app.layout.module';
import { StoreModule } from '@ngrx/store';
import { authReducer } from './shared/state/auth/auth.reducer';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptorService } from './shared/services/interceptors/auth.interceptor.service';
import { SubscriptionInterceptorService } from './shared/services/interceptors/subscription.interceptor.service';
import { AuthInitializerService, initializeAuth } from './shared/services/auth-initializer-service.ts.service';
import { storeReducer } from './shared/state/store/store-reducers';
import { EffectsModule } from '@ngrx/effects';
import { StoreEffects } from './shared/state/store/store-effects';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import * as Sentry from '@sentry/angular';
import { AuthEffects } from './shared/state/auth/auth.effects';
import { Router } from '@angular/router';
import { ticketCategoriesReducer } from './shared/state/ticket-categories/ticket-categories.reducer';
import { TicketCategoriesEffects } from './shared/state/ticket-categories/ticket-categories.effects';
import { ProfileEffects } from './shared/state/profile/profile.effects';
import { profileReducer } from './shared/state/profile/profile.reducer';
import { SubscriptionEffects } from './shared/state/subscription/subscription.effects';
import { subscriptionReducer } from './shared/state/subscription/subscription.reducer';
import { stopWizardReducer } from './features/shift-management/stop-shift-wizard/store/stop-wizard.reducer';

const sentryGuard: boolean = environment.production;

if (sentryGuard) {
  Sentry.init({
    dsn: 'https://d99608d1472b085441951941f3ed4ac9@o4508904787476480.ingest.de.sentry.io/4508904789377104',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }), // ✅ Corrected usage
      Sentry.captureConsoleIntegration({
        levels: ['error'], // Specify which console levels to capture
      }),
    ],
    tracesSampleRate: sentryGuard ? 1.0 : 0.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/betbooks\.gr:8080/, // Your production backend
      /^http:\/\/localhost:8080/, // Your development backend
      /^https:\/\/betbooks\.gr:8081/, // Your production backend
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: environment.name,
    debug: true,
  });
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    AppLayoutModule,
    StoreModule.forRoot({ auth: authReducer, store: storeReducer }),
    EffectsModule.forRoot([StoreEffects, AuthEffects]), // This ensures the effects are listening
    StoreModule.forFeature('ticketCategories', ticketCategoriesReducer),
    EffectsModule.forFeature([TicketCategoriesEffects]),
    StoreModule.forFeature('profile', profileReducer),
    EffectsModule.forFeature([ProfileEffects]),
    StoreModule.forFeature('subscription', subscriptionReducer),
    EffectsModule.forFeature([SubscriptionEffects]),
    StoreModule.forFeature('stopShiftWizard', stopWizardReducer),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retain last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode in production
    }),
    ToastModule,
  ],
  providers: [
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SubscriptionInterceptorService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthInitializerService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router], // ✅ Required for performance monitoring
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {}, // ✅ Ensures TraceService is initialized early
      deps: [Sentry.TraceService],
      multi: true,
    },
    MessageService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
